import { useAtomValue } from 'jotai';
import { Link2, Link2Off, LogOut } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLogout } from '@/hooks/Auth';
import { WebSocketService } from '@/services/websocket';
import { currentUserAtom, sidebarCollapseAtom } from '@/store/atoms';
import { cn } from '@/utils/utils';

// WebSocket status hook
const useWebSocketStatus = () => {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const webSocketService = WebSocketService.getInstance();

    // Subscribe to connection status changes
    const unsubscribe =
      webSocketService.subscribeToConnectionStatus(setIsConnected);

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  return isConnected;
};

export const UserInfo = () => {
  const isCollapsed = useAtomValue(sidebarCollapseAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const isWsConnected = useWebSocketStatus();
  const { logout } = useLogout();

  const userDisplayName = currentUser
    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
      currentUser.email
    : 'User';
  const userInitial = currentUser?.firstName[0] ?? currentUser?.email[0] ?? 'U';

  const userContent = (
    <div
      className={cn(
        'border-sidebar-border border-t p-4',
        'transition-all duration-300',
        isCollapsed ? 'px-2' : 'px-4',
      )}
    >
      <Popover>
        <PopoverTrigger className="w-full">
          <div
            className={cn(
              'bg-muted flex cursor-pointer items-center rounded-md p-2 hover:bg-gray-200/40',
              isCollapsed ? 'justify-center' : 'gap-3',
            )}
          >
            <div className="relative">
              <div className="bg-primary text-primary-foreground relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-medium uppercase">
                {userInitial}
                {isCollapsed && (
                  <div className="absolute -right-1 -bottom-1">
                    <div
                      className={cn(
                        'h-3 w-3 rounded-full border-2 border-white dark:border-gray-900',
                        isWsConnected
                          ? 'bg-green-500'
                          : 'animate-pulse bg-red-500',
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
            {!isCollapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-foreground truncate text-start text-sm font-medium">
                  {userDisplayName}
                </div>
                <div className="text-muted-foreground truncate text-start text-xs">
                  {currentUser?.email}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  {isWsConnected ? (
                    <>
                      <Link2 className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-600">
                        WS Connected
                      </span>
                    </>
                  ) : (
                    <>
                      <Link2Off className="h-3 w-3 animate-pulse text-red-500" />
                      <span className="text-xs text-red-500">
                        WS Disconnected
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="h-fit w-40 p-1.5">
          <Button
            variant={'ghost'}
            className="flex w-full cursor-pointer items-center justify-start gap-2 text-red-600 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              logout();
            }}
          >
            <LogOut className="size-4 text-red-600" />
            Logout
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{userContent}</TooltipTrigger>
        <TooltipContent side="right" className="ml-2">
          <div>
            <div className="font-medium">{userDisplayName}</div>
            <div className="text-muted-foreground text-xs">
              {currentUser?.email}
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              {isWsConnected ? (
                <>
                  <Link2 className="h-3 w-3" />
                  <span className="text-xs">WS Connected</span>
                </>
              ) : (
                <>
                  <Link2Off className="h-3 w-3" />
                  <span className="text-xs">WS Disconnected</span>
                </>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return userContent;
};
